import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BusinessModeService } from '@core/services/business-mode.service';
import { AssistantChatStore } from '../../services/assistant-chat.store';
import {
  ASSISTANT_MAX_MESSAGE_LENGTH,
  AssistantErrorCode,
  RECEIPT_ACCEPT,
  ReceiptErrorCode,
} from '../../models/assistant.model';
import { prepareReceipt } from '../../services/receipt-image';
import { EzriTextPartsPipe } from '../../services/ezri-links';

const SUGGESTION_COUNT = 3;
const INTRO_ITEM_COUNT = 5;
const FEEDBACK_THANKS_MS = 1600;
const FEEDBACK_MAX_LENGTH = 2000;

/** Asking what the user thought, before the panel closes or a new conversation starts. */
type FeedbackState = 'off' | 'asking' | 'sending' | 'thanks';
const MESSAGES_LEFT_WARNING = 5;
const MOBILE_QUERY = '(max-width: 599.98px)';
/** A phone or tablet, where the camera is a tap away. */
const TOUCH_QUERY = '(pointer: coarse)';

/** Card fields whose value is a code with a translation, not text to show as is. */
const ENUM_FIELDS = new Set(['payment_method', 'receipt_document']);

/** ReceiptOptions.MaxFileMb on the server; a bigger file is refused before it is uploaded. */
export const RECEIPT_MAX_BYTES = 10 * 1024 * 1024;

/** Synthetic receipts for the demo (assets/demo/receipts), read through the real scan. */
export const SAMPLE_RECEIPTS = ['sample-cafe.png', 'sample-fuel.png', 'sample-office.pdf'] as const;

/**
 * Ezri's chat panel: transcript, suggestions, confirmation cards and the
 * composer. State lives in AssistantChatStore, so closing the panel keeps
 * the conversation.
 */
@Component({
  selector: 'app-assistant-chat',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, TranslateModule, EzriTextPartsPipe],
  templateUrl: './assistant-chat.component.html',
  styleUrls: ['./assistant-chat.component.scss'],
})
export class AssistantChatComponent implements AfterViewInit {
  readonly store = inject(AssistantChatStore);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly businessMode = inject(BusinessModeService);

  /** Move focus into the input when the panel opens (off for the demo auto-open). */
  readonly autoFocus = input(true);
  /** A receipt can be attached (AssistantConfig.receipts). */
  readonly receipts = input(false);
  /** Free plan messages left this month when the panel loaded (AssistantConfig.monthlyMessagesLeft). */
  readonly monthlyMessagesLeft = input<number | null>(null);
  /** A demo account: offers a sample receipt to try. */
  readonly demo = input(false);
  readonly closed = output<void>();

  private readonly inputRef = viewChild<ElementRef<HTMLTextAreaElement>>('messageInput');
  private readonly scrollerRef = viewChild<ElementRef<HTMLElement>>('scroller');
  private readonly fileInputRef = viewChild<ElementRef<HTMLInputElement>>('fileInput');
  private readonly cameraInputRef = viewChild<ElementRef<HTMLInputElement>>('cameraInput');
  private readonly attachButtonRef = viewChild<ElementRef<HTMLButtonElement>>('attachButton');

  readonly receiptAccept = RECEIPT_ACCEPT;
  readonly attachMenuOpen = signal(false);
  readonly isTouch = matches(TOUCH_QUERY);
  private static nextSample = 0;

  readonly maxLength = ASSISTANT_MAX_MESSAGE_LENGTH;
  readonly draft = this.store.draft;

  readonly feedbackMaxLength = FEEDBACK_MAX_LENGTH;
  readonly stars = [1, 2, 3, 4, 5];
  readonly feedback = signal<FeedbackState>('off');
  readonly rating = signal(0);
  readonly feedbackComment = signal('');
  /** What the user was doing when we asked: carried out once they answer or skip. */
  private pendingExit: 'close' | 'new' | null = null;
  private feedbackTimer: ReturnType<typeof setTimeout> | null = null;

  readonly introKeys = Array.from({ length: INTRO_ITEM_COUNT }, (_, i) => `assistant.intro.items.${i}`);

  // The demo's suggestions name its sample project, client and matter; a real account
  // has none of them, so it gets the same asks without the names.
  readonly suggestionKeys = computed(() => {
    const mode = this.businessMode.businessMode() === 'legal' ? 'legal' : 'freelance';
    const set = this.demo() ? mode : `general.${mode}`;
    return Array.from({ length: SUGGESTION_COUNT }, (_, i) => `assistant.suggestions.${set}.${i}`);
  });

  readonly showMessagesLeft = computed(() => {
    const left = this.store.messagesLeft();
    return left !== null && left > 0 && left <= MESSAGES_LEFT_WARNING && !this.store.limitReached();
  });

  /** The last reply's count is newer than the one the panel loaded with. */
  readonly monthlyLeft = computed(() => this.store.monthlyMessagesLeft() ?? this.monthlyMessagesLeft());

  readonly monthlyLimitReached = computed(() => this.monthlyLeft() === 0);

  readonly canSend = computed(() =>
    !this.store.sending() && !this.store.limitReached() && !this.monthlyLimitReached() &&
    (this.draft().trim().length > 0 || this.store.stagedReceipt() !== null));

  readonly canAttach = computed(() =>
    this.receipts() && !this.store.sending() && !this.store.limitReached() && !this.monthlyLimitReached());

  constructor() {
    // Keep the newest message in view.
    effect(() => {
      this.store.entries();
      this.store.sending();
      setTimeout(() => this.scrollToBottom());
    });
  }

  ngAfterViewInit(): void {
    this.scrollToBottom();
    if (this.autoFocus()) this.focusInput();
  }

  close(): void {
    if (this.attachMenuOpen()) {
      this.closeAttachMenu(true);   // Escape closes the menu first
      return;
    }
    if (this.feedback() !== 'off') {
      this.finishFeedback();   // Escape or X while asking: that is a "not now"
      return;
    }
    if (this.askFeedback('close')) return;
    this.closed.emit();
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' || event.shiftKey) return;
    // An IME (Japanese, Chinese, Korean...) uses Enter to commit a composition.
    if (event.isComposing || event.keyCode === 229) return;
    event.preventDefault();
    this.send();
  }

  async send(text?: string): Promise<void> {
    let message = (text ?? this.draft()).trim().slice(0, this.maxLength);
    // A staged receipt is a message of its own.
    if (!message && text === undefined && this.store.stagedReceipt()) message = this.receiptMessage('');
    if (!message || this.store.sending() || this.store.limitReached() || this.monthlyLimitReached()) return;
    if (text === undefined) this.draft.set('');
    const error = await this.store.send(message, this.uiLanguage());
    // The message did not go through: give it back so it can be resent or edited.
    if (error && !this.draft()) this.draft.set(message);
    // Typed messages keep the focus; a tapped suggestion does not pop up a phone keyboard.
    if (text === undefined) this.focusInput();
  }

  // ---------- receipts ----------

  toggleAttachMenu(): void {
    if (!this.canAttach()) return;
    this.attachMenuOpen.update(open => !open);
  }

  closeAttachMenu(refocus = false): void {
    this.attachMenuOpen.set(false);
    if (refocus) setTimeout(() => this.attachButtonRef()?.nativeElement.focus());
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.attachMenuOpen()) return;
    const target = event.target as Node | null;
    const host = this.attachButtonRef()?.nativeElement.closest('.ezri-attach');
    if (target && host?.contains(target)) return;
    this.attachMenuOpen.set(false);
  }

  takePhoto(): void {
    this.closeAttachMenu();
    this.cameraInputRef()?.nativeElement.click();
  }

  chooseFile(): void {
    this.closeAttachMenu();
    this.fileInputRef()?.nativeElement.click();
  }

  async onFileChosen(event: Event): Promise<void> {
    const inputEl = event.target as HTMLInputElement;
    const file = inputEl.files?.[0];
    // The same file can be chosen again after an error.
    inputEl.value = '';
    if (file) await this.sendReceipt(file);
  }

  async trySample(): Promise<void> {
    this.closeAttachMenu();
    const name = SAMPLE_RECEIPTS[AssistantChatComponent.nextSample++ % SAMPLE_RECEIPTS.length];
    let file: File;
    try {
      const response = await fetch(`assets/demo/receipts/${name}`);
      if (!response.ok) throw new Error(String(response.status));
      const blob = await response.blob();
      file = new File([blob], name, { type: blob.type || (name.endsWith('.pdf') ? 'application/pdf' : 'image/png') });
    } catch {
      this.store.showReceiptError('generic');
      return;
    }
    await this.sendReceipt(file);
  }

  private async sendReceipt(file: File): Promise<void> {
    if (this.store.sending() || this.store.limitReached() || this.monthlyLimitReached()) return;
    const prepared = await prepareReceipt(file);
    if (prepared.blob.size > RECEIPT_MAX_BYTES) {
      this.store.showReceiptError('file_too_large');
      return;
    }
    const typed = this.draft().trim().slice(0, this.maxLength);
    this.draft.set('');
    const error = await this.store.sendReceipt(prepared.blob, prepared.name, this.receiptMessage(typed), this.uiLanguage());
    // What the user typed is theirs to resend; the default text is not.
    if (error && typed && !this.draft()) this.draft.set(typed);
  }

  /** The user's words, or the default request for a receipt with none. */
  private receiptMessage(typed: string): string {
    return typed || this.textOr('assistant.receipt.defaultMessage', 'Record an expense from this receipt');
  }

  thumbnail(entryId: number): string | null {
    return this.store.thumbnails().get(entryId) ?? null;
  }

  isScanning(entryId: number): boolean {
    return this.store.scanningEntryId() === entryId;
  }

  receiptErrorText(code: ReceiptErrorCode): string {
    return this.textOr(`assistant.receipt.errors.${code}`, this.translate.instant('assistant.receipt.errors.generic'));
  }

  useSuggestion(key: string): void {
    void this.send(this.translate.instant(key));
  }

  confirm(entryId: number): void {
    void this.store.confirm(entryId);
  }

  cancel(entryId: number): void {
    void this.store.cancel(entryId);
  }

  newConversation(): void {
    if (this.askFeedback('new')) return;
    // The draft stays: after a limit error it holds the message that did not go through.
    this.store.newConversation();
    this.focusInput();
  }

  setRating(value: number): void {
    if (this.feedback() === 'asking') this.rating.set(value);
  }

  async sendFeedback(): Promise<void> {
    if (this.feedback() !== 'asking' || this.rating() < 1) return;
    this.feedback.set('sending');
    // Thanks either way: a failed send must not keep the user from leaving.
    await this.store.submitFeedback(this.rating(), this.feedbackComment().slice(0, FEEDBACK_MAX_LENGTH), this.uiLanguage());
    if (this.feedback() !== 'sending') return;
    this.feedback.set('thanks');
    this.feedbackTimer = setTimeout(() => this.finishFeedback(), FEEDBACK_THANKS_MS);
  }

  skipFeedback(): void {
    this.finishFeedback();
  }

  /** Starts the question when this conversation deserves one; false to go on at once. */
  private askFeedback(exit: 'close' | 'new'): boolean {
    if (this.store.sending() || !this.store.shouldAskFeedback()) return false;
    this.store.markFeedbackAsked();
    this.pendingExit = exit;
    this.rating.set(0);
    this.feedbackComment.set('');
    this.feedback.set('asking');
    return true;
  }

  private finishFeedback(): void {
    if (this.feedbackTimer !== null) {
      clearTimeout(this.feedbackTimer);
      this.feedbackTimer = null;
    }
    const exit = this.pendingExit;
    this.pendingExit = null;
    this.feedback.set('off');
    if (exit === 'close') {
      this.closed.emit();
    } else if (exit === 'new') {
      this.store.newConversation();
      this.focusInput();
    }
  }

  openLink(link: string | null | undefined): void {
    // Only app routes: a link is a path like /finance/invoices/123.
    if (!link || !link.startsWith('/') || link.startsWith('//')) return;
    void this.router.navigateByUrl(link);
    if (this.isMobile()) this.close();
  }

  actionTitle(tool: string): string {
    return this.textOr(`assistant.actions.${tool}`, tool);
  }

  fieldLabel(key: string): string {
    return this.textOr(`assistant.fields.${key}`, key);
  }

  // The API sends booleans as "true"/"false"; the app's existing yes/no words read better on the card.
  // A field with a fixed set of values (a payment method) sends a code, worded from assistant.values.{key}.{code}.
  fieldValue(value: string, key?: string): string {
    if (value === 'true') return this.textOr('time_tracking.yes', value);
    if (value === 'false') return this.textOr('time_tracking.no', value);
    if (key && ENUM_FIELDS.has(key)) return this.textOr(`assistant.values.${key}.${value}`, value);
    return value;
  }

  /** "done", or a tool's own way of being done (record_payment's done_no_receipt). */
  isDone(code: string | undefined): boolean {
    return code === 'done' || !!code?.startsWith('done_');
  }

  // A tool may word its own outcome: a sent reminder is not "saved".
  resultText(code: string | undefined, tool?: string): string {
    const generic = this.textOr(`assistant.results.${code}`, this.translate.instant('assistant.results.failed'));
    return tool ? this.textOr(`assistant.results.${tool}_${code}`, generic) : generic;
  }

  errorText(code: AssistantErrorCode | undefined): string {
    return this.textOr(`assistant.errors.${code}`, this.translate.instant('assistant.errors.generic'));
  }

  private textOr(key: string, fallback: string): string {
    const text = this.translate.instant(key);
    return typeof text === 'string' && text !== key ? text : fallback;
  }

  private uiLanguage(): string | undefined {
    return this.translate.currentLang || this.translate.defaultLang || undefined;
  }

  private focusInput(): void {
    setTimeout(() => this.inputRef()?.nativeElement.focus());
  }

  private scrollToBottom(): void {
    const el = this.scrollerRef()?.nativeElement;
    if (el) el.scrollTop = el.scrollHeight;
  }

  private isMobile(): boolean {
    return matches(MOBILE_QUERY);
  }
}

function matches(query: string): boolean {
  try { return window.matchMedia(query).matches; } catch { return false; }
}
